import { useMemo } from 'react';
import { useAccessGroups } from '../../resources/ResourcesRouter.js';

/**
 * useCalendarPermissions — exposes the calendar permission contract for the
 * Month View (and future Week/Day/Stacked/Dispatch views).
 *
 * Reads exclusively from the `AccessGroupsProvider` context (no
 * `window.serverData`, no `globals.manageOrganizations`). Per Master Plan,
 * `orgAccessMap` is treated as a per-org map, NEVER a boolean — it falls back
 * to an empty object if the provider has not yet been extended to populate it
 * (the contract is preserved either way).
 *
 * Returns a stable object whose function members are memoised on the access
 * group identity, so consumers can safely use them in deps arrays.
 *
 * @returns {{
 *   canManageMy: boolean,
 *   canManageAnothers: boolean,
 *   canDelete: boolean,
 *   canManageRecurrent: boolean,
 *   canCheckOut: boolean,
 *   canManageMaintenance: boolean,
 *   orgAccessMap: Object,
 *   isOwner: (event: Object, user: Object) => boolean,
 *   canEditEvent: (event: Object, user: Object) => boolean,
 *   canMoveEvent: (event: Object, user: Object) => boolean,
 *   shouldMaskRestricted: (event: Object) => boolean,
 *   _loaded: boolean,
 * }}
 */
export function useCalendarPermissions() {
	const accessGroups = useAccessGroups() || {};
	return useMemo(() => buildCalendarPermissions(accessGroups), [accessGroups]);
}

// ────────────────────────────────────────────────────────────────────────────
// Pure helpers (exported for unit tests)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Pure builder used by both the hook and unit tests.
 * @param {Object} accessGroups Flat boolean map (+ optional `orgAccessMap`).
 */
export function buildCalendarPermissions(accessGroups = {}) {
	const canManageMy = !!accessGroups.calendarManageMy;
	const canManageAnothers = !!accessGroups.calendarManageAnothers;
	const canDelete = !!accessGroups.calendarEventDelete;
	const canManageRecurrent = !!accessGroups.calendarManageRecurrentEvent;
	const canCheckOut = !!accessGroups.checkOutAircraft;
	// Dual permission per legacy intent: needs BOTH to manage maintenance events
	const canManageMaintenance = !!accessGroups.resourcesManage && !!accessGroups.maintenanceManage;
	// Resource subpanel permissions (legacy access group keys)
	const canViewResource = !!accessGroups.resourceView;
	const canViewMeters = !!accessGroups.meterView;
	const canViewInspections = !!accessGroups.inspectionView;
	const canViewDiscrepancies = !!accessGroups.discrepanciesView;
	// Counter (meter value) edit — legacy `calendar.event-resource.js:242`
	// gates the Start "Edit" link on `userIsAPilot || accessgroups.counterEdit`.
	// The pilot-bypass is event-scoped so it's added at the call site (like
	// `canSeeMeters`); here we expose only the access-group boolean.
	const canEditCounter = !!accessGroups.counterEdit;
	// Discrepancy action permissions (Task 23)
	const canManageDiscrepancies = !!accessGroups.discrepanciesManage;
	const canChangeDiscrepancyStatus = !!accessGroups.discrepanciesStatus;
	const canCommentDiscrepancy = !!accessGroups.discrepanciesComment;
	const canDeleteDiscrepancy = !!accessGroups.discrepanciesDelete;
	const canAddDiscrepancy = !!accessGroups.discrepanciesAdd;
	// orgAccessMap is an object, NOT a boolean — keep it that way
	const orgAccessMap =
		accessGroups.orgAccessMap && typeof accessGroups.orgAccessMap === 'object'
			? accessGroups.orgAccessMap
			: {};

	return {
		canManageMy,
		canManageAnothers,
		canDelete,
		canManageRecurrent,
		canCheckOut,
		canManageMaintenance,
		canViewResource,
		canViewMeters,
		canViewInspections,
		canViewDiscrepancies,
		canEditCounter,
		canManageDiscrepancies,
		canChangeDiscrepancyStatus,
		canCommentDiscrepancy,
		canDeleteDiscrepancy,
		canAddDiscrepancy,
		orgAccessMap,
		isOwner: (event, user) => isOwner(event, user),
		canEditEvent: (event, user) => canEditEvent(event, user, { canManageMy, canManageAnothers }),
		canMoveEvent: (event, user) => canMoveEvent(event, user, { canManageMy, canManageAnothers }),
		shouldMaskRestricted: event => shouldMaskRestricted(event, { canManageAnothers }),
		_loaded: !!accessGroups._loaded
	};
}

/**
 * Owner = `pilotID === user.id` OR the user is listed as the event instructor.
 * Numeric vs string ID coercion is handled.
 */
export function isOwner(event, user) {
	if (!event || !user) return false;
	const userId = user.id ?? user.userId ?? user._userID;
	if (userId == null) return false;
	const sameAs = id => id != null && String(id) === String(userId);

	if (sameAs(event.pilotID)) return true;
	if (sameAs(event.instructorID)) return true;
	if (Array.isArray(event.instructorIDs) && event.instructorIDs.some(sameAs)) return true;
	return false;
}

/**
 * Composed edit-permission check, mirroring `factories/calendar.js:262`
 * (`canEditResources`) but reading from the AccessGroupsProvider rather than
 * legacy globals. Honors `availableForEdit` if present.
 */
export function canEditEvent(event, user, perms) {
	if (!event) return false;
	if (event.availableForEdit === false || event.availableForEdit === 0) return false;
	if (isOwner(event, user)) return !!perms.canManageMy;
	return !!perms.canManageAnothers;
}

/**
 * DnD eligibility — same gates as edit, plus we never let restricted-but-not-
 * owned events be moved (since the user can't see the details).
 */
export function canMoveEvent(event, user, perms) {
	if (!canEditEvent(event, user, perms)) return false;
	if (shouldMaskRestricted(event, perms) && !isOwner(event, user)) return false;
	return true;
}

/**
 * Restricted-event masking. Hide details when:
 *   - the event (or its resource) is marked restricted, AND
 *   - the user does NOT have `calendarManageAnothers`.
 */
export function shouldMaskRestricted(event, perms) {
	if (!event) return false;
	const restricted =
		event.restricted === true ||
		event.restricted === 1 ||
		event.restricted === '1' ||
		event.resourceRestricted === true ||
		event.resourceRestricted === 1 ||
		event.resourceRestricted === '1';
	if (!restricted) return false;
	return !perms.canManageAnothers;
}
